import { useMemo, useState } from "react";

import { areFilterIdsEqual, normalizeId } from "utils/filterUtils";

import FilterSection from "./FilterSection";
import SearchInput from "./SearchInput";

import { Checkbox, Icon } from "@/design-system";

/**
 * Rows shown before the "Show N more" toggle. The reference's `BRAND_CAP` is 6 (grep
 * `BRAND_CAP` in `Dens N Dente Tailwind.dc.html`); 12 is a deliberate app-side bump —
 * our facets are far longer than its dozen-brand fixture, so the capped list carries
 * twice the reference's rows before the toggle.
 *
 * This replaces the 204px inner scroller the list used to have. The reference's own dated
 * note is "no search field, no inner scroll — per review, Aug 6", and its filter markup has
 * no `max-h` / `overflow-y` anywhere: the rail's only scroll container is the `<aside>`.
 */
const VISIBLE_CAP = 12;

export default function CollapsibleCheckboxFilter({
  title,
  items = [],
  idKey,
  nameKey,
  selectedIds = [],
  onSelectionChange,
  onClear,
  searchPlaceholder = "Search...",
  searchAriaLabel,
  searchable = true,
  defaultOpen = true,
  renderEmptyMessage,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showAll, setShowAll] = useState(false);

  // **`items` order is preserved — do not sort selected entries to the top.** The
  // reference's rail keeps a ticked row exactly where it was: grep `__brandFilters` in
  // `Dens N Dente Tailwind.dc.html` and it builds the list with `.filter().map()` and no
  // `.sort()`. Its rendered DOM confirms it — the checked brands stay at whatever
  // position they already held.
  //
  // A selected-first sort re-orders the list on the same render as the click, which moves
  // the row you just ticked and whatever you were about to click next. That was the bug;
  // this is the fix. (The reference has since paired selected-first with pinning above a
  // hairline instead — see the filter rail notes in `dnd-frontend-ui`.)
  const filteredItems = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return items;

    return items.filter((item) =>
      String(item[nameKey] || "")
        .toLowerCase()
        .includes(query)
    );
  }, [items, nameKey, searchTerm]);

  // The cap applies to the filtered list too, so the rail stays short whether or not a
  // search is narrowing it.
  const visibleItems = showAll
    ? filteredItems
    : filteredItems.slice(0, VISIBLE_CAP);
  const hiddenCount = filteredItems.length - visibleItems.length;

  const selectedItem =
    selectedIds.length === 1
      ? items.find((item) => areFilterIdsEqual(item[idKey], selectedIds[0]))
      : null;
  const summary =
    selectedIds.length === 1
      ? String(selectedItem?.[nameKey] || "1 selected")
      : selectedIds.length > 1
        ? `${selectedIds.length} selected`
        : "";

  const isChecked = (item) =>
    selectedIds.some((id) => areFilterIdsEqual(id, item[idKey]));

  const clearSelection = () => {
    setSearchTerm("");
    if (onClear) {
      onClear();
      return;
    }

    selectedIds.forEach((id) => onSelectionChange?.(id, false));
  };

  return (
    <FilterSection
      title={title}
      summary={summary}
      onClear={summary ? clearSelection : undefined}
      clearAriaLabel={`Clear ${title.toLowerCase()} filters`}
      defaultOpen={defaultOpen}
      contentClassName="flex flex-col gap-[var(--space-2)] px-[8px] pb-[4px] pt-[6px]"
    >
      {searchable && (
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={searchPlaceholder}
          aria-label={searchAriaLabel || searchPlaceholder}
        />
      )}

      {/* No height limit and no scroller, at either size — the rail's only scroll
          container is the page's `<aside>`. Expanding a large facet therefore makes a long
          column: measured, expanding all 313 brands puts the card at ~8500px. That is the
          intended behaviour here; use the search field to narrow first. */}
      <div className="flex flex-col gap-[var(--space-2)]">
        {filteredItems.length === 0 ? (
          renderEmptyMessage ? (
            renderEmptyMessage()
          ) : (
            <p className="py-[4px] text-2xs text-ink-3">
              No {title.toLowerCase()} match
              {searchTerm ? ` “${searchTerm}”` : ""} — try a shorter term.
            </p>
          )
        ) : (
          visibleItems.map((item) => {
            const itemId = item[idKey];
            const itemName = item[nameKey];

            return (
              <Checkbox
                key={`${title}-${String(itemId)}`}
                size="sm"
                shape="round"
                label={itemName}
                checked={isChecked(item)}
                onChange={(checked) =>
                  onSelectionChange?.(normalizeId(itemId), checked)
                }
                className="w-full flex-row-reverse items-center justify-between"
              />
            );
          })
        )}
      </div>

      {(hiddenCount > 0 || showAll) && (
        <button
          type="button"
          onClick={() => setShowAll((current) => !current)}
          className="inline-flex cursor-pointer items-center gap-[3px] self-start border-0 bg-transparent p-0 font-sans text-2xs font-semibold text-blue-700 [transition:color_var(--duration-fast)] hover:text-blue-800 focus-visible:rounded-sm focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
        >
          <Icon
            name="expand_more"
            size={15}
            weight={400}
            className={`[transition:transform_var(--duration-base)_var(--ease-spring)] ${
              showAll ? "rotate-180" : ""
            }`}
          />
          {showAll ? "Show less" : `Show ${hiddenCount} more`}
        </button>
      )}
    </FilterSection>
  );
}
